import { Fragment } from "react";

import { Icon, Tabs, TabsList, TabsTrigger } from "@nexus/ui";
import { useTranslation } from "react-i18next";

import type {
  PersonaOpportunityTabCounts,
  PersonaOpportunityTabKey,
} from "../../../types/persona-opportunities.types.js";

export interface PersonaOpportunitiesStatusTabsProps {
  counts: PersonaOpportunityTabCounts;
  activeTab: PersonaOpportunityTabKey;
  onTabChange: (tab: PersonaOpportunityTabKey) => void;
}

const TAB_KEYS: PersonaOpportunityTabKey[] = ["opportunities", "scripts", "sent_to_vendor"];

const TAB_I18N_MAP: Record<PersonaOpportunityTabKey, { key: string; fallback: string }> = {
  opportunities: {
    key: "personaOpportunities.tabs.opportunities",
    fallback: "Opportunities",
  },
  scripts: {
    key: "personaOpportunities.tabs.scripts",
    fallback: "Scripts",
  },
  sent_to_vendor: {
    key: "personaOpportunities.tabs.sentToVendor",
    fallback: "Production",
  },
};

export function PersonaOpportunitiesStatusTabs({
  counts,
  activeTab,
  onTabChange,
}: PersonaOpportunitiesStatusTabsProps) {
  const { t } = useTranslation();
  const lastIndex = TAB_KEYS.length - 1;

  return (
    <Tabs
      className="shrink-0 gap-0"
      value={activeTab}
      onValueChange={(value) => onTabChange(value as PersonaOpportunityTabKey)}
    >
      <TabsList
        variant="line"
        className="h-auto w-full justify-start gap-1 group-data-[orientation=horizontal]/tabs:h-auto"
      >
        {TAB_KEYS.map((key, index) => {
          const label = t(TAB_I18N_MAP[key].key, TAB_I18N_MAP[key].fallback);
          return (
            <Fragment key={key}>
              <TabsTrigger
                value={key}
                aria-label={`${label} ${counts[key]}`}
                className="items-baseline gap-2 rounded-none rounded-t-sm px-3 pt-2 pb-3 text-sm data-[state=active]:font-semibold"
              >
                {label}
                <span className="text-xs tabular-nums tracking-tight">{counts[key]}</span>
              </TabsTrigger>
              {index < lastIndex ? (
                <Icon
                  name="chevron-right"
                  size="md"
                  color="muted"
                  strokeWidth={1.25}
                  className="self-center pb-1"
                />
              ) : null}
            </Fragment>
          );
        })}
      </TabsList>
    </Tabs>
  );
}
